import React from 'react';

import { amount, ChangeArrow } from './commitmentFormat';

/** A share of the latest estimate as a bar width, clamped so an overspend still fits the track. */
const share = (value, latestEstimate) => (latestEstimate > 0 ? Math.min(Math.max((value / latestEstimate) * 100, 0), 100) : 0);

/**
 * Where each department and cost centre the commitment touches stands against its
 * latest estimate: committed already, what this commitment adds, and what stays
 * uncommitted.
 */
export default function CommitmentPositionCard({ rows }) {
    if (!rows?.length) {
        return null;
    }

    return (
        <div className="tw-rounded-[10px] tw-border tw-border-charcoal-15 tw-px-4 tw-py-3.5">
            <div className="bc-eyebrow">{$t('Commitment position after this approval')}</div>
            <div className="tw-flex tw-flex-col tw-gap-3">
                {rows.map((row) => (
                    <PositionRow key={`${row.kind}-${row.id}`} row={row} />
                ))}
            </div>
            <div className="bc-sof-leg tw-mt-3">
                <span>
                    <span className="tw-me-1.5 tw-inline-block tw-h-[9px] tw-w-[9px] tw-rounded-sm tw-bg-blue" aria-hidden="true" />
                    {$t('Committed now')}
                </span>
                <span>
                    <span className="tw-me-1.5 tw-inline-block tw-h-[9px] tw-w-[9px] tw-rounded-sm tw-bg-blue-50" aria-hidden="true" />
                    {$t('This commitment')}
                </span>
                <span>
                    <span className="tw-me-1.5 tw-inline-block tw-h-[9px] tw-w-[9px] tw-rounded-sm tw-bg-[#EDEEF0]" aria-hidden="true" />
                    {$t('Uncommitted')}
                </span>
            </div>
        </div>
    );
}

function PositionRow({ row }) {
    const before = share(row.committed_before, row.latest_estimate);
    const added = Math.min(share(row.committed_after, row.latest_estimate) - before, 100 - before);
    const kindLabel = row.kind === 'department' ? $t('department') : $t('cost centre');

    return (
        <div className="tw-grid tw-grid-cols-1 tw-items-center tw-gap-x-4 tw-gap-y-1.5 md:tw-grid-cols-[minmax(0,10rem)_1fr_auto]">
            <div className="tw-min-w-0">
                <div className="tw-text-[13px] tw-font-medium tw-text-charcoal">
                    {row.url ? (
                        <a href={row.url} target="_blank" rel="noopener noreferrer" className="tw-text-charcoal hover:tw-text-blue">
                            {row.name}
                        </a>
                    ) : (
                        row.name
                    )}
                </div>
                <div className="bc-sub">{$t('{{kind}} · latest estimate {{value}}', { kind: kindLabel, value: amount(row.latest_estimate) })}</div>
            </div>
            <div
                className="bc-sof-bar tw-mb-0"
                role="img"
                aria-label={$t('{{before}}% to {{after}}% of latest estimate', { before: row.percent_before ?? 0, after: row.percent_after ?? 0 })}
            >
                <div className="tw-bg-blue" style={{ width: `${before}%` }} />
                <div className="tw-bg-blue-50" style={{ width: `${Math.max(added, 0)}%` }} />
            </div>
            <div className="tw-text-end">
                <div className="tw-text-[13px] tw-font-semibold tw-tabular-nums tw-text-charcoal">
                    {amount(row.committed_before)} <ChangeArrow /> {amount(row.committed_after)}
                </div>
                {row.percent_before !== null && (
                    <div className="bc-sub">
                        {row.percent_before}% <ChangeArrow /> {$t('{{percent}}% of latest estimate', { percent: row.percent_after })}
                    </div>
                )}
            </div>
        </div>
    );
}
